// JobPostBase.js
import React, { useState, useEffect, useRef, Fragment } from 'react';
import { Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { jobPostApi } from '@/_services/api/jobPost';
import JobPostTableContainer from './JobPostTableContainer';
import JobPostFilters from './JobPostFilters';
import { debounce, getTeamIdFromURL } from '@/_helpers';
import NavigationBar from '@/components/Admin/reusableComponents/NavigationBar';
import WelcomeUser from '@/components/Admin/reusableComponents/WelcomeUser';

const JobPostBase = ({ isAdmin = false, teamDetails }) => {
    const [loading, setLoading] = useState(true);
    const [isActive, setIsActive] = useState(true);
    const [data, setData] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [searchText, setSearchText] = useState('');
    const [searchType, setSearchType] = useState(null);
    const teamId = isAdmin ? undefined : getTeamIdFromURL();
    const inputRef = useRef();

    const onPageChanged = ({ currentPage } = {}) => {
        setCurrentPage(Math.max(currentPage || 1, 1));
    };

    const onSearchTypeChange = (value) => {
        setSearchType(value);
    };

    const loadJobPosts = async ({ page = 1, searchText, searchType } = {}) => {
        try {
            setLoading(true);
            const params = {
                archived: !isActive,
                page,
                searchText,
                searchType,
            };
            if (searchType) {
                params.searchType = searchType?.value;
            }
            if (!isAdmin && teamId) {
                params.teamId = teamId;
            }

            const response = await jobPostApi.getJobPosts(params);
            setData(response);
        } catch (error) {
            if (error.name === 'AbortError') return;
            toast.error(error?.message);
        } finally {
            setLoading(false);
        }
    };

    const onEdit = (itemObj) => {
        setData((prev) => ({
            ...prev,
            results: (prev.results || []).map((item) => {
                if (item && item?._id && item?._id === itemObj?._id) {
                    return {
                        ...item,
                        ...itemObj,
                    };
                }
                return item;
            }),
        }));
    };

    const fetchOnAction = (page = 1) => {
        if (page === 1) {
            loadJobPosts({ page: 1 });
        } else {
            setCurrentPage(page);
        }
    };

    const onSearch = debounce((val) => {
        setSearchText(val.trimLeft());
    }, 1000);

    useEffect(() => {
        loadJobPosts({ page: currentPage, searchText, searchType });
    }, [isActive, currentPage, searchText, searchType]);

    return (
        <div className={isAdmin ? 'submitted-form-styled' : 'h-100 d-flex flex-column'}>
            {isAdmin && (
                <div className="d-flex justify-content-between mb-4">
                    <NavigationBar mainTabName="Job Posts" />
                    <WelcomeUser />
                </div>
            )}

            <Fragment>
                <JobPostFilters
                    onSearch={onSearch}
                    isActive={isActive}
                    setIsActive={() => {
                        setIsActive(!isActive);
                        setCurrentPage(1);
                    }}
                    fetchOnAction={fetchOnAction}
                    disabled={loading}
                    inputRef={inputRef}
                    onSearchTypeChange={onSearchTypeChange}
                                    teamDetails={teamDetails}

                />

                {isAdmin ? (
                    <Row>
                        <Col>
                            <div className="custom-card card-table d-flex flex-column justify-content-between">
                                <JobPostTableContainer
                                    teamDetails={teamDetails}
                                    loading={loading}
                                    data={data}
                                    currentPage={currentPage}
                                    onPageChanged={onPageChanged}
                                    onEdit={onEdit}
                                />
                            </div>
                        </Col>
                    </Row>
                ) : (
                    <JobPostTableContainer
                        teamDetails={teamDetails}
                        loading={loading}
                        onEdit={onEdit}
                        fetchOnAction={fetchOnAction}
                        data={data}
                        currentPage={currentPage}
                        onPageChanged={onPageChanged}
                    />
                )}
            </Fragment>
        </div>
    );
};

export default JobPostBase;
